<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="mx-auto mt-5" outlined tile>
          <v-toolbar flat>
            <v-select
              label="Filter by module"
              v-model="moduleId"
              :items="modules"
              item-text="code"
              item-value="_id"
              clearable
              hide-details
              single-line
              class="mr-2"
            />
            <v-select
              label="Filter by unit"
              :disabled="!moduleId"
              v-model="unitCodes"
              :items="units"
              item-text="code"
              item-value="code"
              clearable
              hide-details
              single-line
              multiple
              class="mr-2"
            />
            <v-text-field
              label="Search be venue or Session ID"
              type="text"
              prepend-icon="search"
              v-model="search"
              clearable
              hide-details
              single-line
            />
            <v-spacer />
            <v-switch
              v-model="showOld"
              hide-details
              label="Show old sessions"
              class="mr-2"
            />
            <v-btn text color="primary" @click="add">
              <v-icon>add</v-icon>
              Add
            </v-btn>
          </v-toolbar>

          <v-data-table
            dense
            disable-filtering
            item-key="_id"
            mobile-breakpoint="0"
            :headers="this.headers"
            :options.sync="options"
            :footer-props="footerProps"
            :server-items-length="this.sessions.count"
            :items="this.sessions.items"
            :loading="$apollo.queries.sessions.loading"
          >
            <template v-slot:item="{ item }">
              <tr>
                <td>{{ item.trainingSessionId }}</td>
                <td>{{ item.scheduledDate | formatDate }}</td>
                <td>{{ item.completionDate | formatDate }}</td>
                <td nowrap>{{ _.get(item, 'module.code') }}</td>
                <td nowrap>{{ _.get(item, 'trainingUnitCodes', []).join(', ') }}</td>
                <td nowrap>{{ item.venue || 'No venue' }}</td>
                <td nowrap>{{ _.get(item, 'trainer.person.name') }}</td>
                <td>{{ item.trainees.length }}</td>
                <td class="text-end">
                  <v-btn small icon @click="editSession(item)">
                    <v-icon color="primary">edit</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      v-model="sessionDialog"
      width="400"
    >
      <v-card>
        <v-card-title>
          New Training Session
        </v-card-title>
        <v-card-text>
          <session-form
            v-model="sessionForm"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="createSession"
          >
            Add
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="sessionDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import gql from 'graphql-tag'
import { set } from 'lodash'

import gqlSortMixin from '../mixins/gql-sort-mixin'
import SessionForm from '../components/forms/SessionForm.vue'

export default {
  name: 'AdminTrainingSessionsView',

  components: { SessionForm },

  mixins: [
    gqlSortMixin
  ],

  data: () => ({
    showOld: true,
    search: '',
    sessions: {
      count: null,
      items: []
    },
    headers: [
      {
        text: 'Training Session ID',
        value: 'trainingSessionId'
      },
      {
        text: 'Scheduled',
        value: 'scheduledDate'
      },
      {
        text: 'Completed',
        value: 'completionDate'
      },
      {
        text: 'Module',
        value: 'module.code',
        sortable: false
      },
      {
        text: 'Units',
        value: 'trainingUnitCodes',
        sortable: false
      },
      {
        text: 'Venue',
        value: 'venue'
      },
      {
        text: 'Trainer',
        value: 'trainer.person.name',
        sortable: false
      },
      {
        text: 'Trainees',
        value: 'trainees.length',
        sortable: false
      },
      {
        text: 'Actions',
        value: 'actions',
        class: 'text-end',
        sortable: false
      }
    ],
    options: {
      multiSort: false,
      mustSort: true,
      sortDesc: [true],
      sortBy: ['scheduledDate'],
      page: 1,
      itemsPerPage: 25
    },
    footerProps: {
      'items-per-page-options': [10, 25, 50, 100, 200, 500]
    },
    sessionForm: {},
    sessionDialog: false,
    moduleId: null,
    modules: [],
    unitCodes: []
  }),

  computed: {
    units () {
      const module = this.modules.find(o => o._id === this.moduleId)
      return module?.units ?? []
    }
  },

  watch: {
    moduleId () { // Clear unitCode when moduleId is changed
      this.unitCodes = []
    }
  },

  apollo: {
    sessions: {
      query: gql`query TrainingSessionList($filter: FilterFindManyTrainingSessionInput!, $sort: SortFindManyTrainingSessionInput!, $page: Int!, $perPage: Int!) {
        sessions: trainingSessionManyPagination(page: $page, perPage: $perPage, filter: $filter, sort: $sort) {
          count
          items {
            _id
            trainingSessionId
            scheduledDate
            completionDate
            trainingUnitCodes
            venue
            moduleId
            module {
              _id
              code
            }
            trainerId
            trainer {
              _id
              person {
                _id
                name
              }
            }
            trainees {
              _id
            }
          }
        }
      }`,
      variables () {
        const filter = {}

        if (!this.showOld) {
          set(filter, '_operators.completionDate.gt', new Date())
        }

        if (this.search) {
          const regex = `/${this.search}/gi`
          filter.OR = [{
            _operators: {
              venue: { regex }
            }
          }]
          if (isFinite(this.search)) {
            filter.OR.push({
              trainingSessionId: parseInt(this.search, 10)
            })
          }
        }

        if (this.moduleId) {
          filter.moduleId = this.moduleId
        }

        if (this.unitCodes.length) {
          filter.trainingUnitCodeAll = this.unitCodes
        }

        return {
          filter,
          page: this.options.page,
          perPage: this.options.itemsPerPage,
          sort: this.gqlSort(this.options)
        }
      },
      debounce: 250,
      error (e) { this.onError(e) }
    },

    modules: {
      query: gql`query TrainingModulesList {
        modules: trainingModuleMany(limit: 0, sort: CODE_ASC, filter: { active: true }) {
          _id
          code
          units {
            _id
            code
          }
        }
      }`,
      error (e) { this.onError(e) }
    }
  },

  methods: {
    onError (e) {
      this.$emit('error', e)
    },

    editSession (session) {
      this.$router.push({ name: 'AdminTrainingSession', params: { id: session._id } })
    },

    add () {
      this.sessionForm = {}
      this.sessionDialog = true
    },

    async createSession () {
      try {
        const res = await this.$apollo.mutate({
          mutation: gql`mutation ($record: CreateOneTrainingSessionInput!) {
            trainingSessionCreateOne(record: $record){
              recordId
            }
          }`,
          variables: {
            record: {
              moduleId: this.sessionForm.moduleId,
              trainingUnitCodes: this.sessionForm.trainingUnitCodes,
              trainerId: this.sessionForm.trainerId,
              venue: this.sessionForm.venue,
              scheduledDate: this.sessionForm.scheduledDate,
              completionDate: this.sessionForm.completionDate
            }
          }
        })
        this.$router.push({ name: 'AdminTrainingSession', params: { id: res.data.trainingSessionCreateOne.recordId } })
      } catch (e) {
        this.onError(e)
      }
    }
  }
}
</script>
